import * as React from "react";
import type { ObjectField } from "../api/contracts/objects";

/**
 * How the object peek routes PROSE descriptors, and how it renders the read-only
 * ones. Split out of `object-peek.tsx` for one reason: this is the decision that
 * was wrong in #43855, and in that file it could only be checked by reading it.
 * Here it is a pure function over a field list plus a component with no Next
 * imports, so a test can assert the OUTPUT rather than assert that the source
 * still contains a regex.
 *
 * THE DEFECT, stated once so it cannot come back quietly. The peek has two prose
 * slots — the unlabelled body under the title, and the Outcome section — and both
 * hand their field to `edit.renderProse`. That callback belongs to the edit
 * provider, so for a field it may not write its only honest answer is the
 * fallback it was handed: `obj.description` / `obj.targetOutcome`. Selecting a
 * LOCKED prose descriptor into either slot therefore had two effects at once: the
 * field was pulled out of the property rows (the slot owns it) and then replaced
 * on screen by an unrelated string. Every locked prose descriptor in the product
 * was computed, passed through the contract, and dropped at the last inch —
 * which is precisely the shape of the bug the hiring task was filed about (a
 * rejection reason compelled on write and never readable), reproduced one layer
 * up in the renderer.
 *
 * The rule that closes it: THE BODY SLOT IS THE EDITABLE SLOT. A locked prose
 * field is not a body — it is read-only prose with something to say — so it never
 * competes for that slot and gets its own labelled section instead.
 */

/**
 * Is this descriptor read-only prose? `longtext` is the vocabulary's own word
 * for "this is a paragraph", and a non-null `lockedReason` is the vocabulary's
 * own word for "this may not be written". Deliberately NOT keyed on the field's
 * `key` or the object's kind — the kind-name sniffing this registry exists to
 * delete would put us back to one renderer per kind.
 *
 * `placement` is not consulted: it was an inert contract property (written by
 * the descriptor helper, read by no renderer) and was removed in #44163. A rule
 * leaning on it would be leaning on a flag that no longer exists.
 */
export function isLockedProse(field: ObjectField): boolean {
  return field.type === "longtext" && field.lockedReason != null;
}

export interface ProseSlots {
  /**
   * The EDITABLE body — the peek's unlabelled paragraph under the title. First
   * unlocked, non-outcome `longtext` wins; a kind with two shows the second as a
   * row, which is at least visible rather than dropped. Never a locked field.
   */
  body: ObjectField | null;
  /**
   * The outcome descriptor, locked or not. Its section renders both — an
   * editable outcome through the provider, a locked one read-only — so unlike
   * `body` this slot does not filter locked fields out.
   */
  outcome: ObjectField | null;
  /**
   * Locked prose with something to say: one labelled read-only section each.
   *
   * EMPTY VALUES ARE DROPPED, and that is not the same fail-open the defect
   * above was. A locked field with no value cannot be filled in (nothing can
   * write it) and has no text to show, so a section for it would be a labelled
   * blank — the "empty rows drop themselves" rule the property rail already
   * follows. A locked field WITH a value is never dropped by anything here.
   */
  readOnly: ObjectField[];
}

/**
 * Route a kind's descriptors into the peek's three prose slots. Pure: same list
 * in, same slots out, no DOM, no provider, no object.
 */
export function selectProseSlots(fields: readonly ObjectField[]): ProseSlots {
  const outcome = fields.find((f) => f.outcome) ?? null;
  const body =
    fields.find((f) => f.type === "longtext" && !f.outcome && !isLockedProse(f)) ??
    null;
  const readOnly = fields.filter(
    (f) => isLockedProse(f) && !f.outcome && String(f.value ?? "").trim() !== "",
  );
  return { body, outcome, readOnly };
}

/**
 * A prose descriptor the kind declared READ-ONLY, rendered as ITS OWN value.
 *
 * A locked field is not asking for a control, so this needs none. It needs the
 * paragraph — wrapped, selectable, at body size — and one muted line saying why
 * it cannot be edited, which is the treatment a locked TITLE already gets from
 * `renderTitleStatus`.
 *
 * THE LABEL IS THE CALLER'S JOB. Both call sites wrap this in a
 * `PeekDialogSection`, so the heading comes from the section primitive rather
 * than a hand-rolled `<label>` (Frontend rule 12 — fields are label-led and the
 * label primitive is never re-implemented).
 *
 * `whitespace-pre-wrap` + `break-words`: a rejection reason is typed by a human
 * into a note box, so it can carry its own line breaks and a long unbroken URL.
 */
export function ReadOnlyProse({ field }: { field: ObjectField }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="whitespace-pre-wrap break-words type-body leading-body text-foreground">
        {String(field.value ?? "")}
      </p>
      {field.lockedReason ? (
        <p className="type-subtext leading-body text-muted-foreground">
          {field.lockedReason}
        </p>
      ) : null}
    </div>
  );
}
